import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AgentLayout, { agentFetch } from './AgentLayout';
import { useKeyboardShortcuts, ShortcutsHelpModal, ShortcutsHint } from '../../utils/useKeyboardShortcuts';
import { useAgentDirectory, ReferentTag } from '../../utils/useAgentDirectory';

const CLIENTS_SHORTCUTS = [
  { combo: 'N',     label: 'Créer un nouveau dossier' },
  { combo: 'E',     label: 'Rechercher un client existant' },
  { combo: '/',     label: 'Mettre le focus sur la recherche' },
  { combo: 'Échap', label: 'Revenir au choix précédent' },
  { combo: '?',     label: 'Afficher cette aide' },
];

export default function AgentClients() {
  const navigate = useNavigate();
  const [mode, setMode]         = useState(null); // null | 'new' | 'existing'
  const [form, setForm]         = useState({ prenom: '', nom: '', telephone: '' });
  const [saving, setSaving]     = useState(false);
  const [query, setQuery]       = useState('');
  const [clients, setClients]   = useState([]);
  const [searching, setSearching] = useState(false);
  const [truncated, setTruncated] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const searchInputRef          = useRef(null);
  // Compteur de génération anti-course : la frappe debouncée déclenche
  // plusieurs requêtes ; une réponse lente (terme plus court, partie plus
  // tôt) peut revenir APRÈS une plus récente et écraser ses résultats. Le
  // compteur invalide toute réponse dont la génération n'est plus la
  // dernière.
  const searchGenRef            = useRef(0);
  // Visibilité globale : les résultats mêlent les clients de toute
  // l'équipe — l'annuaire résout assignedAgentId → référent lisible.
  const { referentLabel } = useAgentDirectory();

  // Recherche serveur (2026-06-25) : GET /api/agent/leads?q=<terme> filtre
  // nom/email/téléphone côté Mongo (insensible casse + accents ; téléphone
  // tolérant au format) et renvoie `total` = nombre de correspondances. Un
  // SEUL appel remplace l'ancienne boucle de pagination : plus de plafond de
  // complétude, ÷10 de trafic, aucun client « invisible ».
  //
  // Trois gardes critiques (le but de la feature est d'ÉVITER les fiches en
  // double — une recherche qui échoue silencieusement provoque l'inverse) :
  //  1. requête vide → on n'interroge pas le serveur (pas d'auto-chargement
  //     de 100 fiches arbitraires, pas de bandeau trompeur).
  //  2. `r.ok` vérifié → une 401 (session expirée) ou 500 ne doit JAMAIS être
  //     rendue comme « Aucun résultat » : on lève → `searchError` → message
  //     explicite invitant à réessayer plutôt qu'à créer un doublon.
  //  3. `truncated` (> 100 correspondances) n'a de sens qu'avec un terme.
  const search = useCallback(() => {
    const gen = ++searchGenRef.current;
    const isStale = () => gen !== searchGenRef.current;
    const q = query.trim();
    if (!q) {
      setClients([]); setTruncated(false); setSearchError(false); setSearching(false);
      return;
    }
    setSearching(true);
    setSearchError(false);
    const url = `/api/agent/leads?limit=100&q=${encodeURIComponent(q)}`;
    (async () => {
      const r = await agentFetch(url);
      if (!r.ok) throw new Error(`Recherche échouée (HTTP ${r.status})`);
      const data = await r.json();
      if (isStale()) return; // une recherche plus récente a pris la main
      const list = Array.isArray(data?.items) ? data.items : [];
      setTruncated(Boolean(data?.nextCursor));
      setClients(list);
    })()
      .catch(() => { if (!isStale()) { setClients([]); setTruncated(false); setSearchError(true); } })
      .finally(() => { if (!isStale()) setSearching(false); });
  }, [query]);

  // Debounce 300 ms : `search` change à chaque frappe (dep [query]). Le
  // timer est annulé par la frappe suivante. Requête vide gérée dans search().
  useEffect(() => {
    if (mode !== 'existing') return undefined;
    const timer = setTimeout(search, 300);
    return () => clearTimeout(timer);
  }, [mode, search]);

  // État propre à chaque sortie du mode « Client existant » : évite qu'une
  // ancienne liste de résultats (client précédent) « flashe » au retour.
  useEffect(() => {
    if (mode !== 'existing') {
      setQuery(''); setClients([]); setTruncated(false); setSearchError(false);
    }
  }, [mode]);

  // ── Raccourcis clavier ──────────────────────────────────────────────────
  useKeyboardShortcuts([
    {
      key: 'Escape',
      allowInInput: true,
      handler: () => {
        if (helpOpen) { setHelpOpen(false); return; }
        if (mode) setMode(null);
      },
    },
    {
      key: '?',
      allowInInput: true,
      handler: () => setHelpOpen(o => !o),
    },
    {
      key: 'n',
      handler: () => { if (!mode) setMode('new'); },
    },
    {
      key: 'e',
      handler: () => { if (!mode) setMode('existing'); },
    },
    {
      key: '/',
      handler: () => {
        if (mode !== 'existing') setMode('existing');
        // focus se fait au prochain render via autoFocus / fallback ref
        requestAnimationFrame(() => searchInputRef.current?.focus());
      },
    },
  ], [mode, helpOpen]);

  const createAndStart = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Phase 6 / G2 — POST /api/agent/leads attend { nom, source,
      // email?, telephone? }. On concatène prénom + nom dans `nom`
      // (LeadDto ne distingue plus prénom/nom — le formulaire d'entretien
      // collectera le détail).
      const nom = [form.prenom.trim(), form.nom.trim()].filter(Boolean).join(' ');
      const body = {
        nom: nom || 'Sans nom',
        source: 'appel-entrant',
      };
      const tel = form.telephone.trim();
      if (tel) body.telephone = tel;
      const res = await agentFetch('/api/agent/leads', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Création échouée');
      const lead = await res.json();
      navigate(`/agent/clients/${lead.id}`);
    } catch { setSaving(false); }
  };

  // LeadDto a un seul champ `nom` (string), pas de prénom/nom séparés ni
  // d'aidant_info — l'enrichissement viendra du profil pendant l'entretien.
  const fullName = (c) => c.nom || 'Sans nom';

  const lastDate = (c) => {
    const d = c.updatedAt || c.lastInteractionAt || c.createdAt;
    return d ? new Date(d).toLocaleDateString('fr-FR') : '—';
  };

  return (
    <AgentLayout>
      <div style={{
        maxWidth: 560, margin: '60px auto 0', fontFamily: "'Segoe UI', system-ui, sans-serif",
      }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#2D2D2D', marginBottom: 8 }}>
            Nouvel appel
          </div>
          <div style={{ fontSize: 15, color: '#8A8A8A' }}>
            S'agit-il d'une nouvelle personne ou d'un client existant ?
          </div>
        </div>

        {/* Choix initial */}
        {!mode && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {[
              {
                key: 'new',
                icon: '➕',
                title: 'Nouvelle personne',
                desc: 'Premier contact — créer un nouveau dossier',
                color: '#D4724A',
              },
              {
                key: 'existing',
                icon: '🔍',
                title: 'Client existant',
                desc: 'La personne a déjà appelé — retrouver son dossier',
                color: '#3D8B5E',
              },
            ].map(({ key, icon, title, desc, color }) => (
              <button key={key} onClick={() => setMode(key)} style={{
                padding: '32px 20px', borderRadius: 16, border: `2px solid ${color}20`,
                background: '#FDFCFA', cursor: 'pointer', textAlign: 'center',
                transition: 'all 0.15s',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = color; e.currentTarget.style.background = color + '08'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = color + '20'; e.currentTarget.style.background = '#FDFCFA'; }}
              >
                <div style={{ fontSize: 36, marginBottom: 12 }}>{icon}</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#2D2D2D', marginBottom: 6 }}>{title}</div>
                <div style={{ fontSize: 13, color: '#8A8A8A', lineHeight: 1.4 }}>{desc}</div>
              </button>
            ))}
          </div>
        )}

        {/* Nouvelle personne */}
        {mode === 'new' && (
          <div style={{ background: '#FDFCFA', borderRadius: 16, border: '1px solid #E0DDD8', padding: 28 }}>
            <div style={{ fontWeight: 600, fontSize: 16, color: '#2D2D2D', marginBottom: 4 }}>
              Nouveau dossier
            </div>
            <div style={{ fontSize: 13, color: '#8A8A8A', marginBottom: 20 }}>
              Entrez le minimum — Claude collectera tout le reste pendant l'entretien.
            </div>
            <form onSubmit={createAndStart}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                {[
                  { k: 'prenom', l: 'Prénom', p: 'Marie' },
                  { k: 'nom',    l: 'Nom',    p: 'Dupont' },
                ].map(({ k, l, p }) => (
                  <div key={k}>
                    <label style={{ display: 'block', fontSize: 12, color: '#8A8A8A', marginBottom: 4 }}>{l}</label>
                    <input
                      value={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))}
                      placeholder={p}
                      style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid #D9D9D9', fontSize: 15, boxSizing: 'border-box', outline: 'none' }}
                    />
                  </div>
                ))}
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 12, color: '#8A8A8A', marginBottom: 4 }}>Téléphone <span style={{ color: '#C0C0C0' }}>(optionnel)</span></label>
                <input
                  value={form.telephone} onChange={e => setForm(f => ({ ...f, telephone: e.target.value }))}
                  placeholder="06 12 34 56 78" type="tel"
                  style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid #D9D9D9', fontSize: 15, boxSizing: 'border-box', outline: 'none' }}
                />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="submit" disabled={saving} style={{
                  flex: 1, padding: '12px', borderRadius: 10, border: 'none',
                  background: saving ? '#D9D9D9' : '#D4724A', color: '#fff',
                  fontSize: 15, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer',
                }}>
                  {saving ? 'Création…' : "Démarrer l'entretien →"}
                </button>
                <button type="button" onClick={() => setMode(null)} style={{
                  padding: '12px 18px', borderRadius: 10, border: '1px solid #D9D9D9',
                  background: 'transparent', fontSize: 14, color: '#8A8A8A', cursor: 'pointer',
                }}>
                  ←
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Client existant */}
        {mode === 'existing' && (
          <div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
              <input
                ref={searchInputRef}
                autoFocus
                type="text"
                placeholder="Rechercher par nom, téléphone…"
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && search()}
                style={{
                  flex: 1, padding: '12px 16px', borderRadius: 10, border: '1px solid #D9D9D9',
                  fontSize: 15, outline: 'none',
                }}
              />
              <button onClick={search} style={{
                padding: '12px 20px', borderRadius: 10, border: 'none',
                background: '#3D8B5E', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
              }}>
                Rechercher
              </button>
              <button onClick={() => setMode(null)} style={{
                padding: '12px 14px', borderRadius: 10, border: '1px solid #D9D9D9',
                background: 'transparent', fontSize: 14, color: '#8A8A8A', cursor: 'pointer',
              }}>
                ←
              </button>
            </div>

            {truncated && !searching && (
              <div style={{
                background: '#FFF8EC', border: '1px solid #E8C98A', borderRadius: 10,
                padding: '10px 14px', marginBottom: 10, fontSize: 13, color: '#8A6D2F',
              }}>
                Plus de 100 correspondances — affinez la recherche (nom complet,
                numéro de téléphone) pour retrouver la bonne fiche.
              </div>
            )}
            {searching ? (
              <div style={{ textAlign: 'center', padding: 32, color: '#8A8A8A' }}>Recherche…</div>
            ) : searchError ? (
              <div style={{
                background: '#FDEEEA', borderRadius: 14, border: '1px solid #E8B4A0',
                padding: '24px', textAlign: 'center', color: '#9C4A2E',
              }}>
                <div style={{ fontWeight: 600, marginBottom: 6, color: '#B5462A' }}>
                  La recherche n'a pas pu aboutir.
                </div>
                <div style={{ fontSize: 14, color: '#8A6D5F', lineHeight: 1.5 }}>
                  Vérifiez votre connexion et réessayez.<br />
                  Ne créez pas de nouvelle fiche tant que la recherche n'a pas abouti —
                  le client existe peut-être déjà.
                </div>
                <button onClick={search} style={{
                  marginTop: 14, padding: '8px 18px', borderRadius: 10, border: '1px solid #D4724A',
                  background: '#fff', color: '#D4724A', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                }}>
                  Réessayer
                </button>
              </div>
            ) : clients.length === 0 ? (
              <div style={{
                background: '#FDFCFA', borderRadius: 14, border: '1px solid #E0DDD8',
                padding: '32px', textAlign: 'center', color: '#8A8A8A',
              }}>
                {query ? 'Aucun résultat.' : 'Entrez un nom ou numéro pour rechercher.'}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {clients.map(c => (
                  <div key={c.id} onClick={() => navigate(`/agent/clients/${c.id}`)} style={{
                    background: '#FDFCFA', borderRadius: 12, border: '1px solid #E0DDD8',
                    padding: '14px 18px', cursor: 'pointer', display: 'flex',
                    alignItems: 'center', justifyContent: 'space-between',
                  }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = '#3D8B5E'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = '#E0DDD8'}
                  >
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 15, color: '#2D2D2D', marginBottom: 2 }}>
                        {fullName(c)}
                        {c.statut && (
                          <span style={{ marginLeft: 8, fontSize: 11, background: '#FFF0EA', color: '#D4724A', padding: '2px 7px', borderRadius: 20, fontWeight: 600 }}>
                            {c.statut}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 13, color: '#8A8A8A' }}>
                        {[c.telephone, c.email, c.profil?.departement && `Dpt ${c.profil.departement}`].filter(Boolean).join(' · ')}
                        {' · référent : '}
                        <ReferentTag label={referentLabel(c.assignedAgentId)} />
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', fontSize: 12, color: '#8A8A8A' }}>
                      <div>{lastDate(c)}</div>
                      <div>{c.dossierIds?.length || 0} dossier{(c.dossierIds?.length || 0) !== 1 ? 's' : ''}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <ShortcutsHelpModal
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        shortcuts={CLIENTS_SHORTCUTS}
      />
      <ShortcutsHint onClick={() => setHelpOpen(true)} />
    </AgentLayout>
  );
}
